// TSS (Tradeatory Strategy Script) — the compact frontend language users
// and the AI write. It compiles to canonical TSL (Tradeatory Strategy
// Language), the graph IR — and the ENGINE EXECUTES TSL ONLY.
//
// ════════════════════════════════════════════════════════════════════════
// THE DIVERGENCE GUARANTEE (read this before changing anything)
// ════════════════════════════════════════════════════════════════════════
//
// The script NEVER executes. There is exactly one executor (the TSL engine)
// and exactly one artifact of record (the TSL graph). The script's meaning
// is DEFINED as "whatever compileScript emits" — it has no independent
// semantics to drift. Five rules, enforced by scripts/test-strategy-script.mjs:
//
//   1. SCRIPT NEVER EXECUTES — engine/optimizer/Pine-export consume TSL only.
//   2. SOURCE VIEW IS A PROJECTION — UIs display printGraph(currentGraph),
//      never stored text (stored sourceText is comment-fidelity only and is
//      stale the moment sourceHash ≠ hash of the current graph).
//   3. ROUND-TRIP LAWS — compile(print(g)) ≡ g (semantic graph equivalence,
//      see canonicalizeGraph) for every graph in the corpus, and
//      print(compile(s)) is a fixpoint (normalize ∘ normalize = normalize).
//   4. 1:1 LOWERING — every construct maps to a documented block pattern;
//      anything the printer doesn't recognize prints as a GENERIC block
//      (lossless param list) — printing is total, never approximate.
//   5. EXISTING GATES STAY AUTHORITATIVE — compiled output must pass
//      structuralValidate with zero errors AND autoRepair must be a no-op
//      (repair firing on compiled output = compiler bug; fail closed).
//
// Token economics (the reason this exists): raw TSL JSON is ~55-60%
// structural boilerplate (UUIDs, positions, metadata, mirrored connection
// arrays) and ~35-40% custom-code strings. The script erases the
// boilerplate and replaces KNOWN custom code with one-line `use` registry
// references — measured on the FTMO Sweep fixture: ~13k tokens → ~0.9k.
//
// Deno + browser + node compatible: shared safe-TSL imports, no platform APIs.

// ── Public types ───────────────────────────────────────────────────────────

export interface ScriptDiagnostic {
  line: number;          // 1-based
  col: number;           // 1-based
  message: string;
  expected?: string;     // what the parser wanted ("identifier", "'='", …)
  hint?: string;         // suggested fix when obvious
}

export interface CompileResult {
  ok: boolean;
  blocks: TslBlock[] | null;
  diagnostics: ScriptDiagnostic[];
}

export interface PrintResult {
  text: string;
  // Observability: how much of the graph used sugar vs the generic form.
  stats: { sugared: number; generic: number; total: number };
}

export interface TslBlock {
  id: string;
  type: string;
  data: Record<string, unknown>;
  position: { x: number; y: number };
  connections: { inputs: string[]; outputs: string[] };
  metadata?: Record<string, unknown>;
}

// ── Canonical custom-block registry ────────────────────────────────────────
//
// TSL migration (2026-07-14): PER-BAR entries are authored as SAFE TSL — the
// only custom-block form the engine executes (host JS is quarantined at
// runtime). Each entry's tslText is parsed ONCE at module load; `use
// name(params)` compiles to a custom block carrying the canonical text +
// executable AST, and the printer folds a block back to `use` when its
// canonical printBlock() text matches the registry.
// PER-TRADE entries (qty calculator + risk rules) carry NO body at all: they
// dispatch to the engine's native, parity-pinned implementations via the
// parameters.__canonical marker (src/engine/riskRules.ts
// CANONICAL_CHECKBAR_FNS; src/engine/qtyCalculator.ts
// CANONICAL_FTMO_CALCQTY). Their former JS bodies were display-only after
// that migration and are retired here.
// NEVER change an entry's semantics in place — register a _v2 instead
// (existing graphs must keep printing/compiling identically).

import { parseBlock as parseTslBlockText, printBlock as printTslBlock } from './blockGate/blockText.ts';
import type { TslBlock as SafeTslBlock } from './blockGate/ast.ts';

export interface CanonicalBlockDef {
  id: string;                 // stable registry id (matches data.parameters.__canonical when present)
  blockType: string;          // custom block subtype
  outputTypes: string[];
  /** Safe TSL source — PER-BAR entries only (per-trade entries dispatch natively by marker). */
  tslText?: string;
  defaults?: Record<string, unknown>;
  // One-line behavioral description — rendered into the AI grammar prompt
  // so the model can REASON about a `use` reference without seeing the
  // body (params are the tuning surface; internals are validated).
  description: string;
}

const SWEEP_LONG_TSL = `pattern {
  param lookback: int 2..200 = 30
  value: ((low < prev(lowest(low, lookback), 1)) and (close > prev(lowest(low, lookback), 1))) ? 1 : 0
  signal: (low < prev(lowest(low, lookback), 1)) and (close > prev(lowest(low, lookback), 1))
}`;
const SWEEP_SHORT_TSL = `pattern {
  param lookback: int 2..200 = 30
  value: ((high > prev(highest(high, lookback), 1)) and (close < prev(highest(high, lookback), 1))) ? 1 : 0
  signal: (high > prev(highest(high, lookback), 1)) and (close < prev(highest(high, lookback), 1))
}`;
const DISPLACEMENT_TSL = `filter {
  param atrPeriod: int 2..100 = 14
  param bodyMult: float 0.1..3 = 0.7
  param minAtr: float 0..100000 = 0
  value: abs(close - open)
  signal: (abs(close - open) >= (atr(atrPeriod) * bodyMult)) and (atr(atrPeriod) >= minAtr)
}`;
// htfema1 implements EXACTLY the Pine `ta.ema(close[1], n)` +
// request.security(lookahead_off) semantics the former JS body reproduced
// (see blockGate/ast.ts). Param note: the old JS read `htfFactor` but always
// treated it as MINUTES — the TSL entry names it honestly (htfMinutes).
const HTF_EMA_TSL = `indicator {
  param htfMinutes: int 1..10080 = 2
  param period: int 2..400 = 200
  value: htfema1(htfMinutes, period)
}`;

export const CANONICAL_BLOCKS: CanonicalBlockDef[] = [
  { description: 'true when the bar sweeps below the prior N-bar low and closes back above it (failed liquidity grab, long trigger); param lookback', id: 'sweep_failure_long_v1',  blockType: 'pattern', outputTypes: ['patterns'], tslText: SWEEP_LONG_TSL,  defaults: { lookback: 30 } },
  { description: 'mirror short trigger: sweep above prior N-bar high, close back below; param lookback', id: 'sweep_failure_short_v1', blockType: 'pattern', outputTypes: ['patterns'], tslText: SWEEP_SHORT_TSL, defaults: { lookback: 30 } },
  { description: 'true when bar body >= bodyMult x ATR(atrPeriod) AND ATR >= minAtr (conviction + volatility floor filter)', id: 'displacement_gate_v1',   blockType: 'filter',  outputTypes: ['signals'],  tslText: DISPLACEMENT_TSL, defaults: { atrPeriod: 14, bodyMult: 0.7, minAtr: 0 } },
  { description: 'higher-timeframe EMA(period) with Pine close[1] + request.security(lookahead_off) parity; params period, htfMinutes (HTF bucket size in minutes)', id: 'htf_ema_v1',             blockType: 'indicator', outputTypes: ['value'],  tslText: HTF_EMA_TSL, defaults: { period: 200, htfMinutes: 2 } },
  { description: 'integer-contract position size = min(riskPct% of equity, mdlBufferPct% of remaining daily-loss headroom) / (stopPoints x pointValue + rtCommission), clamped to min/maxContracts — native engine implementation', id: 'ftmo_calcqty_v1',        blockType: 'qty_calculator', outputTypes: ['qty'] },
  { description: 'blocks entries after maxTradesPerDay fills in one local trading day — native engine implementation', id: 'max_trades_per_day_v1',  blockType: 'risk_rule', outputTypes: ['risk_action'] },
  { description: 'halts permanently when realized daily PnL <= -maxDailyLossPct% of accountSize — native engine implementation', id: 'max_daily_loss_pct_v1',  blockType: 'risk_rule', outputTypes: ['risk_action'] },
  { description: 'halts permanently when cumulative PnL <= -maxTotalLossPct% of accountSize — native engine implementation', id: 'max_total_loss_pct_v1',  blockType: 'risk_rule', outputTypes: ['risk_action'] },
  { description: 'halts permanently when equity falls trailingDrawdownPct% below its peak — native engine implementation', id: 'trailing_drawdown_pct_v1', blockType: 'risk_rule', outputTypes: ['risk_action'] },
  { description: 'stops trading for the day once realized daily profit >= dailyProfitLockPct% of day-start equity — native engine implementation', id: 'daily_profit_lock_pct_v1', blockType: 'risk_rule', outputTypes: ['risk_action'] },
  { description: 'flattens any open position on the last in-session bar before sessionEnd (timezone-aware) — native engine implementation', id: 'flatten_at_session_close_v1', blockType: 'risk_rule', outputTypes: ['risk_action'] },
];

const CANONICAL_BY_ID = new Map(CANONICAL_BLOCKS.map(c => [c.id, c]));
// Per-bar entries: parse ONCE at module load (a parse error is a build error,
// never a silently-dead block); fold by the CANONICAL printed text.
export const CANONICAL_TSL: Map<string, { ast: SafeTslBlock; canonicalText: string }> = new Map();
for (const def of CANONICAL_BLOCKS) {
  if (!def.tslText) continue;
  const r = parseTslBlockText(def.tslText);
  if (!r.ok) throw new Error(`canonical block '${def.id}' TSL failed to parse: ${r.errors.map(e => `L${e.line} ${e.error}`).join('; ')}`);
  if (r.block.blockType !== def.blockType) throw new Error(`canonical block '${def.id}' header '${r.block.blockType}' != blockType '${def.blockType}'`);
  CANONICAL_TSL.set(def.id, { ast: r.block, canonicalText: printTslBlock(r.block) });
}
const CANONICAL_BY_TSL = new Map([...CANONICAL_TSL.entries()].map(([id, v]) => [v.canonicalText, CANONICAL_BY_ID.get(id)!]));

// ── TypeScript-subset static check for custom code ─────────────────────────
//
// The runtime sandbox already constrains execution; this rejects forbidden
// constructs BEFORE the code ever ships. Deterministic-only, typed-helpers-
// only. Returns violation strings (empty = clean).

const TS_SUBSET_FORBIDDEN: Array<{ pattern: RegExp; why: string }> = [
  { pattern: /\bimport\s/,           why: 'imports are not allowed in custom code' },
  { pattern: /\brequire\s*\(/,       why: 'require() is not allowed in custom code' },
  { pattern: /\bfetch\s*\(/,         why: 'network access is not allowed in custom code' },
  { pattern: /\bXMLHttpRequest\b/,   why: 'network access is not allowed in custom code' },
  { pattern: /\bDate\s*\.\s*now\b/,  why: 'Date.now is non-deterministic' },
  { pattern: /\bnew\s+Date\b/,       why: 'Date construction is non-deterministic' },
  { pattern: /\bMath\s*\.\s*random\b/, why: 'Math.random is non-deterministic' },
  { pattern: /\basync\b/,            why: 'async is not allowed in custom code' },
  { pattern: /\bawait\b/,            why: 'await is not allowed in custom code' },
  { pattern: /\beval\s*\(/,          why: 'eval is not allowed' },
  { pattern: /\bnew\s+Function\b/,   why: 'Function constructor is not allowed' },
  { pattern: /\bglobalThis\b/,       why: 'global state is not allowed in custom code' },
  { pattern: /\bwindow\b/,           why: 'global state is not allowed in custom code' },
  { pattern: /\bprocess\b/,          why: 'process access is not allowed in custom code' },
  { pattern: /\blocalStorage\b/,     why: 'storage access is not allowed in custom code' },
];

export function checkTsSubset(code: string): string[] {
  const violations: string[] = [];
  // Strip strings/comments first so prose can't false-positive.
  const stripped = code
    .replace(/\/\*[\s\S]*?\*\//g, ' ')
    .replace(/\/\/[^\n]*/g, ' ')
    .replace(/`(?:\\[\s\S]|[^`\\])*`/g, '""')
    .replace(/'(?:\\.|[^'\\\n])*'/g, '""')
    .replace(/"(?:\\.|[^"\\\n])*"/g, '""');
  for (const f of TS_SUBSET_FORBIDDEN) {
    if (f.pattern.test(stripped)) violations.push(f.why);
  }
  return violations;
}

// ── Small utilities ────────────────────────────────────────────────────────

function stableStringify(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return '[' + v.map(stableStringify).join(',') + ']';
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o).sort();
  return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify(o[k])).join(',') + '}';
}

function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

function slugify(name: string): string {
  const words = name.replace(/[^A-Za-z0-9]+/g, ' ').trim().split(/\s+/);
  if (words.length === 0 || !words[0]) return '';
  const parts = words.map((w, i) => i === 0 ? w.toLowerCase() : (w[0].toUpperCase() + w.slice(1).toLowerCase()));
  let s = parts.join('');
  if (/^[0-9]/.test(s)) s = '_' + s;
  return s;
}

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}

function suggest(name: string, candidates: Iterable<string>): string | undefined {
  let best: string | undefined; let bestD = 3;
  for (const c of candidates) {
    const d = levenshtein(name.toLowerCase(), c.toLowerCase());
    if (d < bestD) { bestD = d; best = c; }
  }
  return best;
}

// ── Lexer ──────────────────────────────────────────────────────────────────

interface Token {
  kind: 'ident' | 'number' | 'string' | 'punct' | 'code' | 'newline' | 'eof';
  value: string;
  line: number;
  col: number;
}

const PUNCT = new Set(['{', '}', '(', ')', '[', ']', ',', ':', '=', '.', '@']);
const OPS = ['>=', '<=', '==', '!=', '>', '<'];

function lex(src: string, diagnostics: ScriptDiagnostic[]): Token[] {
  const tokens: Token[] = [];
  let i = 0, line = 1, col = 1;
  const n = src.length;
  const push = (kind: Token['kind'], value: string, l: number, c: number) => tokens.push({ kind, value, line: l, col: c });
  while (i < n) {
    const ch = src[i];
    if (ch === '\n') { push('newline', '\n', line, col); i++; line++; col = 1; continue; }
    if (ch === ' ' || ch === '\t' || ch === '\r') { i++; col++; continue; }
    if (ch === '/' && src[i + 1] === '/') { while (i < n && src[i] !== '\n') { i++; col++; } continue; }
    // ts code body: ```…``` fenced (used inside `ts (...) { ... }` form we
    // lex as a balanced-brace raw capture — see parser; lexer just emits
    // punctuation and the parser switches to raw mode after `ts (…)`).
    if (ch === '"') {
      const l = line, c = col, start = i; i++; col++;
      while (i < n && src[i] !== '"') {
        if (src[i] === '\\') { i += 2; col += 2; continue; }
        if (src[i] === '\n') break;
        i++; col++;
      }
      if (src[i] !== '"') diagnostics.push({ line: l, col: c, message: 'Unterminated string literal', expected: '"' });
      i++; col++;
      try { push('string', JSON.parse(src.slice(start, i)), l, c); }
      catch { diagnostics.push({ line: l, col: c, message: 'Invalid JSON string escape' }); }
      continue;
    }
    const two = src.slice(i, i + 2);
    if (OPS.includes(two)) { push('punct', two, line, col); i += 2; col += 2; continue; }
    if (OPS.includes(ch)) { push('punct', ch, line, col); i++; col++; continue; }
    if (PUNCT.has(ch)) { push('punct', ch, line, col); i++; col++; continue; }
    if (/[0-9]/.test(ch) || (ch === '-' && /[0-9]/.test(src[i + 1] ?? ''))) {
      const l = line, c = col;
      let out = ch; i++; col++;
      while (i < n && /[0-9._eE%+-]/.test(src[i])) {
        // stop '-'/'+' unless exponent context
        if ((src[i] === '-' || src[i] === '+') && !/[eE]/.test(src[i - 1])) break;
        out += src[i]; i++; col++;
      }
      push('number', out, l, c);
      continue;
    }
    if (/[A-Za-z_$]/.test(ch)) {
      const l = line, c = col;
      let out = ch; i++; col++;
      while (i < n && /[A-Za-z0-9_$]/.test(src[i])) { out += src[i]; i++; col++; }
      push('ident', out, l, c);
      continue;
    }
    diagnostics.push({ line, col, message: `Unexpected character '${ch}'` });
    i++; col++;
  }
  push('eof', '', line, col);
  return tokens;
}

/** Capture a balanced-brace raw region starting at index of '{' in src.
 *  Returns [codeText, endIndex]. Honors strings/comments inside. */
function captureBraced(src: string, openIdx: number): [string, number] {
  let depth = 0;
  let i = openIdx;
  const n = src.length;
  let start = -1;
  while (i < n) {
    const ch = src[i];
    if (ch === '"' || ch === "'" || ch === '`') {
      const q = ch; i++;
      while (i < n && src[i] !== q) { if (src[i] === '\\') i++; i++; }
      i++; continue;
    }
    if (ch === '/' && src[i + 1] === '/') { while (i < n && src[i] !== '\n') i++; continue; }
    if (ch === '/' && src[i + 1] === '*') { i += 2; while (i < n && !(src[i] === '*' && src[i + 1] === '/')) i++; i += 2; continue; }
    if (ch === '{') { depth++; if (depth === 1) start = i + 1; }
    else if (ch === '}') { depth--; if (depth === 0) return [src.slice(start, i), i + 1]; }
    i++;
  }
  return ['', n];
}

// ── AST ────────────────────────────────────────────────────────────────────

type Literal = string | number | boolean | null | Literal[] | { [k: string]: Literal };

interface OperandRef { kind: 'price' | 'number' | 'sym'; value: string | number; port?: string; line: number; col: number }
interface CondDef { name: string; left: OperandRef; op: string; right: OperandRef; line: number }
interface IndDef { name: string; blockType: string; params: Record<string, Literal>; line: number }
interface CustomDef {
  name: string | null;
  role: 'pattern' | 'filter' | 'indicator' | 'qty' | 'sl' | 'tp' | 'risk';
  source: { kind: 'use'; canonicalId: string } | { kind: 'ts'; code: string } | { kind: 'tsl'; body: string };
  params: Record<string, Literal>;
  line: number;
}
interface BoolNode { kind: 'sym' | 'and' | 'or' | 'not'; sym?: string; children?: BoolNode[]; line: number; col: number }
interface EntryDef { direction: 'long' | 'short'; when: BoolNode; qty?: string; sl?: string; tp?: string; extras?: Record<string, Literal>; line: number }
interface TerminalDef { kind: 'stop' | 'take'; params: Record<string, Literal>; line: number }
interface RawDef { json: TslBlock; line: number }

interface ScriptAst {
  strategy: { name: string; props: Record<string, Literal> } | null;
  inds: IndDef[];
  conds: CondDef[];
  customs: CustomDef[];
  entries: EntryDef[];
  terminals: TerminalDef[];
  raws: RawDef[];
}

// ── Parser ─────────────────────────────────────────────────────────────────

const INDICATOR_BLOCK_TYPES = new Set([
  'moving_average', 'momentum_oscillator', 'volatility', 'volume', 'trend',
  'chart_patterns', 'candlestick_patterns', 'time_filter', 'price_data',
]);
const PRICE_NAMES = new Set(['open', 'high', 'low', 'close']);
const COMPARATORS = new Set(['>', '>=', '<', '<=', '==', '!=', 'crosses_above', 'crosses_below']);
const CUSTOM_ROLE_TO_BLOCKTYPE: Record<CustomDef['role'], string> = {
  pattern: 'pattern', filter: 'filter', indicator: 'indicator',
  qty: 'qty_calculator', sl: 'sl_calculator', tp: 'tp_calculator', risk: 'risk_rule',
};

class Parser {
  private pos = 0;
  constructor(
    private src: string,
    private tokens: Token[],
    private diagnostics: ScriptDiagnostic[],
  ) {}

  private peek(): Token { return this.tokens[this.pos]; }
  private next(): Token { return this.tokens[this.pos++]; }
  private skipNewlines() { while (this.peek().kind === 'newline') this.pos++; }
  private err(t: Token, message: string, expected?: string, hint?: string) {
    this.diagnostics.push({ line: t.line, col: t.col, message, expected, hint });
  }
  private expectPunct(p: string): boolean {
    const t = this.peek();
    if (t.kind === 'punct' && t.value === p) { this.pos++; return true; }
    this.err(t, `Expected '${p}' but found ${t.kind === 'eof' ? 'end of input' : `'${t.value}'`}`, `'${p}'`);
    return false;
  }
  private expectIdent(what = 'identifier'): string | null {
    const t = this.peek();
    if (t.kind === 'ident') { this.pos++; return t.value; }
    this.err(t, `Expected ${what} but found ${t.kind === 'eof' ? 'end of input' : `'${t.value}'`}`, what);
    return null;
  }
  private skipToLineEnd() {
    while (this.peek().kind !== 'newline' && this.peek().kind !== 'eof') this.pos++;
  }

  /** literal: string | number | true|false|null | [lit,...] | {k: lit, ...} */
  private parseLiteral(): Literal | undefined {
    const t = this.peek();
    if (t.kind === 'string') { this.pos++; return t.value; }
    if (t.kind === 'number') {
      this.pos++;
      const isPct = t.value.endsWith('%');
      const num = Number(isPct ? t.value.slice(0, -1) : t.value);
      if (!Number.isFinite(num)) { this.err(t, `Invalid number '${t.value}'`); return undefined; }
      return num; // '%' is presentation sugar; value is the bare number
    }
    if (t.kind === 'ident') {
      if (t.value === 'true') { this.pos++; return true; }
      if (t.value === 'false') { this.pos++; return false; }
      if (t.value === 'null') { this.pos++; return null; }
      // bare identifier as string literal (enum values: ema, close, american…)
      this.pos++;
      return t.value;
    }
    if (t.kind === 'punct' && t.value === '[') {
      this.pos++;
      const arr: Literal[] = [];
      this.skipNewlines();
      while (!(this.peek().kind === 'punct' && this.peek().value === ']')) {
        const v = this.parseLiteral();
        if (v === undefined) return undefined;
        arr.push(v);
        this.skipNewlines();
        if (this.peek().kind === 'punct' && this.peek().value === ',') { this.pos++; this.skipNewlines(); }
      }
      this.pos++;
      return arr;
    }
    if (t.kind === 'punct' && t.value === '{') {
      this.pos++;
      const obj: { [k: string]: Literal } = {};
      this.skipNewlines();
      while (!(this.peek().kind === 'punct' && this.peek().value === '}')) {
        const keyTok = this.peek();
        let key: string | null = null;
        if (keyTok.kind === 'ident' || keyTok.kind === 'string') { key = keyTok.value; this.pos++; }
        else { this.err(keyTok, 'Expected object key', 'identifier or string'); return undefined; }
        if (!this.expectPunct(':')) return undefined;
        const v = this.parseLiteral();
        if (v === undefined) return undefined;
        obj[key!] = v;
        this.skipNewlines();
        if (this.peek().kind === 'punct' && this.peek().value === ',') { this.pos++; this.skipNewlines(); }
      }
      this.pos++;
      return obj;
    }
    this.err(t, `Expected a value but found ${t.kind === 'eof' ? 'end of input' : `'${t.value}'`}`, 'value');
    return undefined;
  }

  /** (k: lit, k2: lit) — empty () allowed */
  private parseParams(): Record<string, Literal> | null {
    if (!this.expectPunct('(')) return null;
    const out: Record<string, Literal> = {};
    this.skipNewlines();
    while (!(this.peek().kind === 'punct' && this.peek().value === ')')) {
      const keyTok = this.peek();
      const key = this.expectIdent('parameter name');
      if (key === null) return null;
      if (!this.expectPunct(':')) return null;
      const v = this.parseLiteral();
      if (v === undefined) return null;
      if (key in out) this.err(keyTok, `Duplicate parameter '${key}'`);
      out[key] = v;
      this.skipNewlines();
      if (this.peek().kind === 'punct' && this.peek().value === ',') { this.pos++; this.skipNewlines(); }
    }
    this.pos++;
    return out;
  }

  private parseOperand(): OperandRef | null {
    const t = this.peek();
    if (t.kind === 'number') {
      this.pos++;
      const num = Number(t.value.endsWith('%') ? t.value.slice(0, -1) : t.value);
      return { kind: 'number', value: num, line: t.line, col: t.col };
    }
    if (t.kind === 'ident') {
      this.pos++;
      if (PRICE_NAMES.has(t.value)) return { kind: 'price', value: t.value, line: t.line, col: t.col };
      let port: string | undefined;
      if (this.peek().kind === 'punct' && this.peek().value === '.') {
        this.pos++;
        port = this.expectIdent('output port') ?? undefined;
      }
      return { kind: 'sym', value: t.value, port, line: t.line, col: t.col };
    }
    this.err(t, `Expected price (open/high/low/close), a number, or a symbol — found '${t.value}'`, 'operand');
    return null;
  }

  /** boolean expr for `when`: term (and term)* | term (or term)* — mixing
   *  and/or at one level requires parentheses (kept strict on purpose). */
  private parseBool(): BoolNode | null {
    const first = this.parseBoolTerm();
    if (!first) return null;
    const t0 = this.peek();
    if (!(t0.kind === 'ident' && (t0.value === 'and' || t0.value === 'or'))) return first;
    const joiner = t0.value as 'and' | 'or';
    const children: BoolNode[] = [first];
    while (this.peek().kind === 'ident' && (this.peek().value === 'and' || this.peek().value === 'or')) {
      const j = this.next();
      if (j.value !== joiner) {
        this.err(j, `Cannot mix 'and' / 'or' at the same level — use parentheses`, undefined,
          `write e.g. "a and (b or c)"`);
        return null;
      }
      const term = this.parseBoolTerm();
      if (!term) return null;
      children.push(term);
    }
    return { kind: joiner, children, line: first.line, col: first.col };
  }

  private parseBoolTerm(): BoolNode | null {
    const t = this.peek();
    if (t.kind === 'ident' && t.value === 'not') {
      this.pos++;
      const inner = this.parseBoolTerm();
      if (!inner) return null;
      return { kind: 'not', children: [inner], line: t.line, col: t.col };
    }
    if (t.kind === 'punct' && t.value === '(') {
      this.pos++;
      const inner = this.parseBool();
      if (!inner) return null;
      if (!this.expectPunct(')')) return null;
      return inner;
    }
    if (t.kind === 'ident') {
      this.pos++;
      return { kind: 'sym', sym: t.value, line: t.line, col: t.col };
    }
    this.err(t, `Expected a condition name, 'not', or '(' — found '${t.value}'`, 'condition');
    return null;
  }

  parse(): ScriptAst {
    const ast: ScriptAst = { strategy: null, inds: [], conds: [], customs: [], entries: [], terminals: [], raws: [] };
    for (;;) {
      this.skipNewlines();
      const t = this.peek();
      if (t.kind === 'eof') break;
      if (t.kind !== 'ident') {
        this.err(t, `Expected a statement keyword (strategy/ind/cond/pattern/filter/calc/risk/entry/stop/take/raw) — found '${t.value}'`, 'statement');
        this.skipToLineEnd();
        continue;
      }
      switch (t.value) {
        case 'strategy': this.parseStrategy(ast); break;
        case 'ind': this.parseInd(ast); break;
        case 'cond': this.parseCond(ast); break;
        case 'pattern': case 'filter': case 'indicatorfn': this.parseCustomNamed(ast, t.value === 'indicatorfn' ? 'indicator' : (t.value as 'pattern' | 'filter')); break;
        case 'calc': this.parseCalc(ast); break;
        case 'risk': this.parseRisk(ast); break;
        case 'entry': this.parseEntry(ast); break;
        case 'stop': this.parseTerminal(ast, 'stop'); break;
        case 'take': this.parseTerminal(ast, 'take'); break;
        case 'raw': this.parseRaw(ast); break;
        default:
          this.err(t, `Unknown statement '${t.value}'`, 'statement',
            suggest(t.value, ['strategy', 'ind', 'cond', 'pattern', 'filter', 'calc', 'risk', 'entry', 'stop', 'take', 'raw'])
              ? `did you mean '${suggest(t.value, ['strategy', 'ind', 'cond', 'pattern', 'filter', 'calc', 'risk', 'entry', 'stop', 'take', 'raw'])}'?` : undefined);
          this.skipToLineEnd();
      }
    }
    return ast;
  }

  private parseStrategy(ast: ScriptAst) {
    const kw = this.next(); // 'strategy'
    const nameTok = this.peek();
    let name = 'Strategy';
    if (nameTok.kind === 'string') { name = nameTok.value; this.pos++; }
    if (!this.expectPunct('{')) { this.skipToLineEnd(); return; }
    const props: Record<string, Literal> = {};
    for (;;) {
      this.skipNewlines();
      const t = this.peek();
      if (t.kind === 'punct' && t.value === '}') { this.pos++; break; }
      if (t.kind === 'eof') { this.err(t, "Unterminated 'strategy { … }' block", "'}'"); break; }
      const key = this.expectIdent('strategy property');
      if (key === null) { this.skipToLineEnd(); continue; }
      if (!this.expectPunct(':')) { this.skipToLineEnd(); continue; }
      const v = this.parseLiteral();
      if (v === undefined) { this.skipToLineEnd(); continue; }
      props[key] = v;
      // optional trailing comma (props may be comma- or newline-separated)
      if (this.peek().kind === 'punct' && this.peek().value === ',') this.pos++;
    }
    if (ast.strategy) this.err(kw, 'Duplicate strategy block — only one allowed');
    ast.strategy = { name, props };
  }

  private parseInd(ast: ScriptAst) {
    const kw = this.next(); // 'ind'
    const name = this.expectIdent('indicator name');
    if (name === null) { this.skipToLineEnd(); return; }
    if (!this.expectPunct('=')) { this.skipToLineEnd(); return; }
    const t = this.peek();
    if (t.kind === 'ident' && t.value === 'use') {
      this.pos++;
      const cid = this.expectIdent('canonical block id');
      if (cid === null) { this.skipToLineEnd(); return; }
      const params = this.parseParams();
      if (params === null) { this.skipToLineEnd(); return; }
      ast.customs.push({ name, role: 'indicator', source: { kind: 'use', canonicalId: cid }, params, line: kw.line });
      return;
    }
    if (t.kind === 'ident' && (t.value === 'ts' || t.value === 'tsl')) {
      const isTsl = t.value === 'tsl';
      this.pos++;
      const def = isTsl ? this.parseTslBody('indicator', name, kw.line) : this.parseTsBody('indicator', name, kw.line);
      if (def) ast.customs.push(def);
      return;
    }
    const bt = this.expectIdent('block type');
    if (bt === null) { this.skipToLineEnd(); return; }
    if (!INDICATOR_BLOCK_TYPES.has(bt)) {
      this.err(t, `Unknown indicator block type '${bt}'`, 'block type',
        suggest(bt, INDICATOR_BLOCK_TYPES) ? `did you mean '${suggest(bt, INDICATOR_BLOCK_TYPES)}'?` : undefined);
      this.skipToLineEnd(); return;
    }
    const params = this.parseParams();
    if (params === null) { this.skipToLineEnd(); return; }
    ast.inds.push({ name, blockType: bt, params, line: kw.line });
  }

  private parseCond(ast: ScriptAst) {
    const kw = this.next(); // 'cond'
    const name = this.expectIdent('condition name');
    if (name === null) { this.skipToLineEnd(); return; }
    if (!this.expectPunct('=')) { this.skipToLineEnd(); return; }
    const left = this.parseOperand();
    if (!left) { this.skipToLineEnd(); return; }
    const opTok = this.peek();
    let op: string | null = null;
    if (opTok.kind === 'punct' && COMPARATORS.has(opTok.value)) { op = opTok.value; this.pos++; }
    else if (opTok.kind === 'ident' && COMPARATORS.has(opTok.value)) { op = opTok.value; this.pos++; }
    else {
      this.err(opTok, `Expected comparator (> >= < <= == != crosses_above crosses_below) — found '${opTok.value}'`, 'comparator');
      this.skipToLineEnd(); return;
    }
    const right = this.parseOperand();
    if (!right) { this.skipToLineEnd(); return; }
    if (left.kind === 'number') {
      this.err(opTok, 'Literal must be on the RIGHT side of a comparison', undefined, 'swap the operands and mirror the comparator');
      this.skipToLineEnd(); return;
    }
    ast.conds.push({ name, left, op, right, line: kw.line });
  }

  private parseCustomNamed(ast: ScriptAst, role: 'pattern' | 'filter' | 'indicator') {
    const kw = this.next();
    const name = this.expectIdent(`${kw.value} name`);
    if (name === null) { this.skipToLineEnd(); return; }
    if (!this.expectPunct('=')) { this.skipToLineEnd(); return; }
    const t = this.peek();
    if (t.kind === 'ident' && t.value === 'use') {
      this.pos++;
      const cid = this.expectIdent('canonical block id');
      if (cid === null) { this.skipToLineEnd(); return; }
      const params = this.parseParams();
      if (params === null) { this.skipToLineEnd(); return; }
      ast.customs.push({ name, role, source: { kind: 'use', canonicalId: cid }, params, line: kw.line });
      return;
    }
    if (t.kind === 'ident' && (t.value === 'ts' || t.value === 'tsl')) {
      const isTsl = t.value === 'tsl';
      this.pos++;
      const def = isTsl ? this.parseTslBody(role, name, kw.line) : this.parseTsBody(role, name, kw.line);
      if (def) ast.customs.push(def);
      return;
    }
    this.err(t, `Expected 'use <canonical>' or 'tsl (…) { … }'`, "'use' or 'tsl'");
    this.skipToLineEnd();
  }

  private parseCalc(ast: ScriptAst) {
    const kw = this.next(); // 'calc'
    const name = this.expectIdent('calculator name');
    if (name === null) { this.skipToLineEnd(); return; }
    if (!this.expectPunct('=')) { this.skipToLineEnd(); return; }
    // optional role: qty | sl | tp (default qty)
    let role: CustomDef['role'] = 'qty';
    let t = this.peek();
    if (t.kind === 'ident' && (t.value === 'qty' || t.value === 'sl' || t.value === 'tp')) {
      role = t.value as CustomDef['role']; this.pos++; t = this.peek();
    }
    if (t.kind === 'ident' && t.value === 'use') {
      this.pos++;
      const cid = this.expectIdent('canonical block id');
      if (cid === null) { this.skipToLineEnd(); return; }
      const params = this.parseParams();
      if (params === null) { this.skipToLineEnd(); return; }
      ast.customs.push({ name, role, source: { kind: 'use', canonicalId: cid }, params, line: kw.line });
      return;
    }
    if (t.kind === 'ident' && (t.value === 'ts' || t.value === 'tsl')) {
      const isTsl = t.value === 'tsl';
      this.pos++;
      const def = isTsl ? this.parseTslBody(role, name, kw.line) : this.parseTsBody(role, name, kw.line);
      if (def) ast.customs.push(def);
      return;
    }
    this.err(t, `Expected 'use <canonical>' or 'tsl (…) { … }'`, "'use' or 'tsl'");
    this.skipToLineEnd();
  }

  private parseRisk(ast: ScriptAst) {
    const kw = this.next(); // 'risk'
    const t = this.peek();
    if (t.kind === 'ident' && t.value === 'use') {
      this.pos++;
      const cid = this.expectIdent('canonical block id');
      if (cid === null) { this.skipToLineEnd(); return; }
      const params = this.parseParams();
      if (params === null) { this.skipToLineEnd(); return; }
      ast.customs.push({ name: null, role: 'risk', source: { kind: 'use', canonicalId: cid }, params, line: kw.line });
      return;
    }
    if (t.kind === 'ident' && t.value === 'ts') {
      this.pos++;
      const def = this.parseTsBody('risk', null, kw.line);
      if (def) ast.customs.push(def);
      return;
    }
    this.err(t, `Expected 'use <canonical>' or 'ts (…) { … }' after 'risk'`, "'use' or 'ts'");
    this.skipToLineEnd();
  }

  /** After consuming `ts`: parse (params) then a balanced-brace code body. */
  private parseTsBody(role: CustomDef['role'], name: string | null, line: number): CustomDef | null {
    const params = this.parseParams();
    if (params === null) { this.skipToLineEnd(); return null; }
    const braceTok = this.peek();
    if (!(braceTok.kind === 'punct' && braceTok.value === '{')) {
      this.err(braceTok, "Expected '{' to open the custom code body", "'{'");
      this.skipToLineEnd(); return null;
    }
    // Find byte offset of this '{' in the source: re-scan from the token's
    // line/col. We track absolute offsets by reconstructing from line starts.
    const offset = lineColToOffset(this.src, braceTok.line, braceTok.col);
    const [code, endIdx] = captureBraced(this.src, offset);
    // Advance the token stream past the captured region.
    const endLC = offsetToLineCol(this.src, endIdx);
    while (this.peek().kind !== 'eof') {
      const tk = this.peek();
      if (tk.line > endLC.line || (tk.line === endLC.line && tk.col >= endLC.col)) break;
      this.pos++;
    }
    const trimmed = dedent(code);
    void checkTsSubset; void trimmed; void role; void name; void line;
    // TSL migration — host JavaScript custom blocks are no longer executed
    // (quarantined engine-wide). The body is still captured so the error is
    // anchored correctly, then rejected wholesale with the migration path.
    this.diagnostics.push({
      line: braceTok.line, col: braceTok.col,
      message: "Custom code rejected: host JavaScript ('ts { … }') is no longer executed — author the block as safe TSL: `tsl { param …  state …  value: …  signal: … }` (or `use <canonical>`)",
    });
    return null;
  }

  /** After consuming `tsl`: parse optional (param overrides) then a braced body of
   *  SAFE-TSL inner statements (param / state / value / signal lines). The compiler
   *  wraps the body with the role's blockType header and parses it with the same
   *  grammar the engine executes. */
  private parseTslBody(role: CustomDef['role'], name: string | null, line: number): CustomDef | null {
    const params = this.parseParams();
    if (params === null) { this.skipToLineEnd(); return null; }
    const braceTok = this.peek();
    if (!(braceTok.kind === 'punct' && braceTok.value === '{')) {
      this.err(braceTok, "Expected '{' to open the TSL block body", "'{'");
      this.skipToLineEnd(); return null;
    }
    const offset = lineColToOffset(this.src, braceTok.line, braceTok.col);
    const [body, endIdx] = captureBraced(this.src, offset);
    const endLC = offsetToLineCol(this.src, endIdx);
    while (this.peek().kind !== 'eof') {
      const tk = this.peek();
      if (tk.line > endLC.line || (tk.line === endLC.line && tk.col >= endLC.col)) break;
      this.pos++;
    }
    return { name, role, source: { kind: 'tsl', body: dedent(body) }, params, line };
  }

  private parseEntry(ast: ScriptAst) {
    const kw = this.next(); // 'entry'
    const dirTok = this.peek();
    const dir = this.expectIdent("'long' or 'short'");
    if (dir === null || (dir !== 'long' && dir !== 'short')) {
      this.err(dirTok, `Entry direction must be 'long' or 'short' (found '${dir}')`, "'long'|'short'");
      this.skipToLineEnd(); return;
    }
    const whenTok = this.peek();
    if (!(whenTok.kind === 'ident' && whenTok.value === 'when')) {
      this.err(whenTok, `Expected 'when' after entry direction`, "'when'");
      this.skipToLineEnd(); return;
    }
    this.pos++;
    const when = this.parseBool();
    if (!when) { this.skipToLineEnd(); return; }
    const def: EntryDef = { direction: dir as 'long' | 'short', when, line: kw.line };
    // optional clauses: qty <sym> | sl <sym> | tp <sym> | (extra: data, …)
    for (;;) {
      const t = this.peek();
      if (t.kind === 'ident' && (t.value === 'qty' || t.value === 'sl' || t.value === 'tp')) {
        this.pos++;
        const sym = this.expectIdent(`${t.value} calculator symbol`);
        if (sym === null) { this.skipToLineEnd(); return; }
        if (t.value === 'qty') def.qty = sym;
        else if (t.value === 'sl') def.sl = sym;
        else def.tp = sym;
        continue;
      }
      if (t.kind === 'punct' && t.value === '(') {
        const extras = this.parseParams();
        if (extras === null) { this.skipToLineEnd(); return; }
        def.extras = extras;
        continue;
      }
      break;
    }
    ast.entries.push(def);
  }

  private parseTerminal(ast: ScriptAst, kind: 'stop' | 'take') {
    const kw = this.next();
    const params = this.parseParams();
    if (params === null) { this.skipToLineEnd(); return; }
    ast.terminals.push({ kind, params, line: kw.line });
  }

  private parseRaw(ast: ScriptAst) {
    const kw = this.next(); // 'raw'
    const braceTok = this.peek();
    if (!(braceTok.kind === 'punct' && braceTok.value === '{')) {
      this.err(braceTok, "Expected '{' after 'raw' (full block JSON)", "'{'");
      this.skipToLineEnd(); return;
    }
    const offset = lineColToOffset(this.src, braceTok.line, braceTok.col);
    const [inner, endIdx] = captureBraced(this.src, offset);
    const endLC = offsetToLineCol(this.src, endIdx);
    while (this.peek().kind !== 'eof') {
      const tk = this.peek();
      if (tk.line > endLC.line || (tk.line === endLC.line && tk.col >= endLC.col)) break;
      this.pos++;
    }
    try {
      const obj = JSON.parse('{' + inner + '}');
      if (typeof obj.id !== 'string' || !obj.id || typeof obj.type !== 'string' || !obj.type) {
        this.err(kw, 'raw block must include "id" and "type"');
        return;
      }
      if (!obj.data || typeof obj.data !== 'object' || Array.isArray(obj.data) ||
          (obj.connections !== undefined && (!obj.connections ||
            !['inputs', 'outputs'].every(k => Array.isArray(obj.connections[k]) && obj.connections[k].every((id: unknown) => typeof id === 'string'))))) {
        this.err(kw, 'raw block needs an object data map and string-array connections.inputs/outputs');
        return;
      }
      ast.raws.push({ json: obj as TslBlock, line: kw.line });
    } catch (e) {
      this.err(kw, `raw block is not valid JSON: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
}

function lineColToOffset(src: string, line: number, col: number): number {
  let l = 1, i = 0;
  while (l < line && i < src.length) { if (src[i] === '\n') l++; i++; }
  return i + (col - 1);
}
function offsetToLineCol(src: string, offset: number): { line: number; col: number } {
  let line = 1, col = 1;
  for (let i = 0; i < offset && i < src.length; i++) {
    if (src[i] === '\n') { line++; col = 1; } else col++;
  }
  return { line, col };
}
function dedent(code: string): string {
  const lines = code.replace(/^\n/, '').replace(/\s+$/, '').split('\n');
  let min = Infinity;
  for (const l of lines) {
    if (!l.trim()) continue;
    const m = l.match(/^\s*/);
    min = Math.min(min, m ? m[0].length : 0);
  }
  if (!Number.isFinite(min) || min === 0) return lines.join('\n');
  return lines.map(l => l.slice(min)).join('\n');
}

// ── Lowering: AST → TSL blocks ─────────────────────────────────────────────

const COSMETIC_DATA_KEYS = new Set([
  // Display-only fields. Terminal 'logic' is behavioral and must survive.
  'name', 'description', 'alertOnEntry', 'alertOnStopLoss', 'alertOnTakeProfit',
]);

/** Mask the contents of `ts (…) { … }` and `raw { … }` bodies with spaces
 *  (newlines preserved) so the LEXER never sees raw TS/JSON characters.
 *  The PARSER reads bodies from the ORIGINAL source via captureBraced. */
function maskRawRegions(src: string): string {
  const out = src.split('');
  const n = src.length;
  const blank = (a: number, b: number) => { for (let i = a; i < b && i < n; i++) if (out[i] !== '\n') out[i] = ' '; };
  let i = 0;
  while (i < n) {
    // word-boundary `ts` followed by (…){…}  — or `raw` followed by {…}
    if (/[A-Za-z0-9_$]/.test(src[i - 1] ?? ' ')) { i++; continue; }
    // `tsl (…) { … }` (safe-TSL body) and `ts (…) { … }` (legacy JS body —
    // still masked so the migration diagnostic anchors cleanly instead of the
    // lexer spraying per-character errors).
    const kwLen = src.startsWith('tsl', i) && !/[A-Za-z0-9_$]/.test(src[i + 3] ?? ' ') ? 3
      : src.startsWith('ts', i) && !/[A-Za-z0-9_$]/.test(src[i + 2] ?? ' ') ? 2 : 0;
    if (kwLen > 0) {
      let j = i + kwLen;
      while (j < n && /\s/.test(src[j])) j++;
      if (src[j] === '(') {
        let depth = 0;
        while (j < n) { if (src[j] === '(') depth++; else if (src[j] === ')') { depth--; if (depth === 0) { j++; break; } } j++; }
        while (j < n && /\s/.test(src[j])) j++;
        if (src[j] === '{') {
          const [, end] = captureBraced(src, j);
          blank(j + 1, end - 1);
          i = end; continue;
        }
      }
    }
    if (src.startsWith('raw', i) && !/[A-Za-z0-9_$]/.test(src[i + 3] ?? ' ')) {
      let j = i + 3;
      while (j < n && /\s/.test(src[j])) j++;
      if (src[j] === '{') {
        const [, end] = captureBraced(src, j);
        blank(j + 1, end - 1);
        i = end; continue;
      }
    }
    i++;
  }
  return out.join('');
}

export function compileScript(source: string): CompileResult {
  const diagnostics: ScriptDiagnostic[] = [];
  const tokens = lex(maskRawRegions(source), diagnostics);
  const parser = new Parser(source, tokens, diagnostics);
  const ast = parser.parse();
  if (diagnostics.length > 0) return { ok: false, blocks: null, diagnostics };

  const rawConfigs = ast.raws.filter(r => r.json.type === 'strategy_config');
  if (!ast.strategy && rawConfigs.length !== 1) {
    diagnostics.push({ line: 1, col: 1, message: "Missing 'strategy \"Name\" { … }' block", hint: 'every strategy starts with a strategy block' });
  }
  if ((ast.strategy && rawConfigs.length > 0) || rawConfigs.length > 1) {
    diagnostics.push({ line: 1, col: 1, message: 'Strategy must contain exactly one strategy configuration' });
  }
  const rawIds = new Set<string>();
  for (const r of ast.raws) {
    if (rawIds.has(r.json.id)) diagnostics.push({ line: r.line, col: 1, message: `Duplicate raw block id '${r.json.id}'` });
    rawIds.add(r.json.id);
  }
  if (ast.entries.length === 0 && ast.raws.every(r => r.json.type !== 'entry')) {
    diagnostics.push({ line: 1, col: 1, message: 'Strategy must have at least one entry (entry long/short when …)' });
  }
  if (diagnostics.length > 0) return { ok: false, blocks: null, diagnostics };

  const blocks: TslBlock[] = [];
  const symToId = new Map<string, string>();
  const symRole = new Map<string, 'cond' | 'signal' | 'value' | 'calc'>(); // what a symbol can do
  let seq = 0;
  const newId = () => { let id: string; do { id = `s${++seq}`; } while (rawIds.has(id)); return id; };
  let row = 0;
  const pos = (col: number) => ({ x: 60 + col * 380, y: 60 + (row++) * 170 });
  const mk = (type: string, data: Record<string, unknown>, col: number): TslBlock => {
    const b: TslBlock = { id: newId(), type, data, position: pos(col), connections: { inputs: [], outputs: [] } };
    blocks.push(b);
    return b;
  };
  const connect = (from: TslBlock, to: TslBlock) => {
    if (!from.connections.outputs.includes(to.id)) from.connections.outputs.push(to.id);
    if (!to.connections.inputs.includes(from.id)) to.connections.inputs.push(from.id);
  };
  const blockById = (id: string) => blocks.find(b => b.id === id)!;

  // 1. strategy_config (title is validator-required; default to the name)
  if (ast.strategy) {
    const cfgData: Record<string, unknown> = { name: ast.strategy.name, ...structuredCloneLit(ast.strategy.props) as Record<string, unknown> };
    if (cfgData.title === undefined) cfgData.title = ast.strategy.name;
    mk('strategy_config', cfgData, 0);
  }

  // 2. indicators
  for (const ind of ast.inds) {
    if (symToId.has(ind.name)) { diagnostics.push({ line: ind.line, col: 1, message: `Duplicate symbol '${ind.name}'` }); continue; }
    const b = mk(ind.blockType, { name: ind.name, ...(structuredCloneLit(ind.params) as Record<string, unknown>) }, 0);
    symToId.set(ind.name, b.id);
    symRole.set(ind.name, ind.blockType === 'time_filter' ? 'value' : 'value');
  }

  // 3. customs (incl. risk rules + calculators)
  const calcSyms = new Map<string, string>(); // sym → block id (qty/sl/tp)
  for (const c of ast.customs) {
    let outputTypes: string[]; let blockType = CUSTOM_ROLE_TO_BLOCKTYPE[c.role];
    let params = structuredCloneLit(c.params) as Record<string, unknown>;
    // TSL migration — a custom block's payload is the canonical safe-TSL text +
    // executable AST (per-bar), or a bare __canonical marker (per-trade, native
    // engine dispatch). data.code is never emitted.
    let tslText: string | undefined; let tslAst: unknown;
    if (c.source.kind === 'use') {
      const def = CANONICAL_BY_ID.get(c.source.canonicalId);
      if (!def) {
        diagnostics.push({
          line: c.line, col: 1, message: `Unknown canonical block '${c.source.canonicalId}'`,
          hint: suggest(c.source.canonicalId, CANONICAL_BY_ID.keys()) ? `did you mean '${suggest(c.source.canonicalId, CANONICAL_BY_ID.keys())}'?` : undefined,
        });
        continue;
      }
      outputTypes = def.outputTypes;
      if (def.blockType !== blockType) {
        diagnostics.push({ line: c.line, col: 1, message: `Canonical block '${def.id}' is ${def.blockType}, not ${blockType}` });
        continue;
      }
      blockType = def.blockType;
      params = { ...(def.defaults ?? {}), ...params, __canonical: def.id };
      const parsed = CANONICAL_TSL.get(def.id);
      if (parsed) { tslText = parsed.canonicalText; tslAst = parsed.ast; }
    } else if (c.source.kind === 'tsl') {
      const headerType = blockType;
      const wrapped = `${headerType} {
${c.source.body}
}`;
      const r = parseTslBlockText(wrapped);
      if (!r.ok) {
        for (const e of r.errors) {
          diagnostics.push({ line: c.line + Math.max(0, e.line - 1), col: 1, message: `TSL block error: ${e.error}` });
        }
        continue;
      }
      tslAst = r.block;
      tslText = printTslBlock(r.block);
      // parameters = declared defaults overridden by call-site values.
      const declared: Record<string, unknown> = {};
      for (const [k, spec] of Object.entries(r.block.params ?? {})) declared[k] = (spec as { default?: unknown }).default;
      params = { ...declared, ...params };
      outputTypes =
        blockType === 'pattern' ? ['patterns'] :
        blockType === 'filter' ? ['signals'] : ['value'];
    } else {
      // Host JS — the parser already rejects `ts` bodies with the migration
      // diagnostic; this branch is defensive.
      diagnostics.push({ line: c.line, col: 1, message: 'host JavaScript custom blocks are no longer executed — author the block as safe TSL' });
      continue;
    }
    const data: Record<string, unknown> = {
      name: c.name ?? blockType, blockType, parameters: params, outputTypes,
      ...(tslText ? { tslText } : {}), ...(tslAst ? { tslAst } : {}),
    };
    const b = mk('custom', data, 0);
    if (c.name) {
      if (symToId.has(c.name)) { diagnostics.push({ line: c.line, col: 1, message: `Duplicate symbol '${c.name}'` }); continue; }
      symToId.set(c.name, b.id);
      if (c.role === 'pattern' || c.role === 'filter') symRole.set(c.name, 'signal');
      else if (c.role === 'indicator') symRole.set(c.name, 'value');
      else { symRole.set(c.name, 'calc'); calcSyms.set(c.name, b.id); }
    }
  }

  // 4. conditions (expression blocks)
  const operandToFields = (o: OperandRef, side: 'left' | 'right', line: number): Record<string, unknown> | null => {
    if (o.kind === 'price') {
      return side === 'left' ? { leftSource: 'price', leftPriceSource: o.value } : { rightSource: 'price', rightPriceSource: o.value };
    }
    if (o.kind === 'number') {
      if (side === 'left') { diagnostics.push({ line, col: o.col, message: 'Literal must be on the right side' }); return null; }
      return { rightSource: 'static', rightValue: o.value };
    }
    const id = symToId.get(String(o.value));
    if (!id) {
      diagnostics.push({
        line, col: o.col, message: `Unknown symbol '${o.value}'`,
        hint: suggest(String(o.value), symToId.keys()) ? `did you mean '${suggest(String(o.value), symToId.keys())}'?` : undefined,
      });
      return null;
    }
    const port = o.port ?? 'value';
    return side === 'left'
      ? { leftSource: 'input', leftInputBlockId: id, leftOutputPort: port }
      : { rightSource: 'input', rightInputBlockId: id, rightOutputPort: port };
  };

  for (const c of ast.conds) {
    if (symToId.has(c.name)) { diagnostics.push({ line: c.line, col: 1, message: `Duplicate symbol '${c.name}'` }); continue; }
    const lf = operandToFields(c.left, 'left', c.line);
    const rf = operandToFields(c.right, 'right', c.line);
    if (!lf || !rf) continue;
    const data: Record<string, unknown> = { name: c.name, operator: c.op, ...lf, ...rf };
    const b = mk('expression', data, 1);
    // wire input connections from referenced blocks
    for (const o of [c.left, c.right]) {
      if (o.kind === 'sym') {
        const src = blockById(symToId.get(String(o.value))!);
        connect(src, b);
      }
    }
    symToId.set(c.name, b.id);
    symRole.set(c.name, 'cond');
  }

  // 5. entries (+ gates from `when`)
  const lowerBool = (node: BoolNode): TslBlock | null => {
    if (node.kind === 'sym') {
      const id = symToId.get(node.sym!);
      if (!id) {
        diagnostics.push({
          line: node.line, col: node.col, message: `Unknown condition '${node.sym}'`,
          hint: suggest(node.sym!, symToId.keys()) ? `did you mean '${suggest(node.sym!, symToId.keys())}'?` : undefined,
        });
        return null;
      }
      const role = symRole.get(node.sym!);
      if (role !== 'cond' && role !== 'signal') {
        diagnostics.push({
          line: node.line, col: node.col,
          message: `'${node.sym}' is a ${role === 'value' ? 'value series' : 'calculator'} — conditions in 'when' must be boolean (a cond or a pattern/filter)`,
          hint: role === 'value' ? `compare it first: cond x = ${node.sym} > 0` : undefined,
        });
        return null;
      }
      return blockById(id);
    }
    if (node.kind === 'not') {
      const inner = lowerBool(node.children![0]);
      if (!inner) return null;
      const gate = mk('logic_gate', { name: 'NOT', type: 'AND', invert: true }, 2);
      connect(inner, gate);
      return gate;
    }
    const gate = mk('logic_gate', { name: node.kind === 'and' ? 'ALL' : 'ANY', type: node.kind === 'and' ? 'AND' : 'OR', invert: false }, 2);
    for (const ch of node.children!) {
      const inner = lowerBool(ch);
      if (!inner) return null;
      connect(inner, gate);
    }
    return gate;
  };

  for (const e of ast.entries) {
    let src = lowerBool(e.when);
    if (!src) continue;
    // A single custom boolean still needs the required logic boundary before
    // an entry. AND with one input preserves its signal without adding a filter.
    if (src.type === 'custom') {
      const gate = mk('logic_gate', { name: 'ALL', type: 'AND', invert: false }, 2);
      connect(src, gate);
      src = gate;
    }
    const data: Record<string, unknown> = {
      name: `${e.direction} entry`, direction: e.direction, entryType: 'market',
      useStrategyDefaults: !(e.qty || e.sl || e.tp),
      ...(e.extras ? structuredCloneLit(e.extras) as Record<string, unknown> : {}),
    };
    const entry = mk('entry', data, 3);
    connect(src, entry);
    for (const [field, sym] of [['qtyCalculatorBlockId', e.qty], ['slCalculatorBlockId', e.sl], ['tpCalculatorBlockId', e.tp]] as const) {
      if (!sym) continue;
      const calcId = calcSyms.get(sym);
      if (!calcId) {
        diagnostics.push({
          line: e.line, col: 1, message: `Unknown calculator '${sym}' in entry clause`,
          hint: calcSyms.size > 0 ? `known calculators: ${[...calcSyms.keys()].join(', ')}` : 'define one with: calc name = ts qty (…) { … }',
        });
        continue;
      }
      entry.data[field] = calcId;
      // NOTE: per-trade calculators dispatch via the id POINTER, not canvas
      // edges (sizing.ts contract; structuralValidate V-3/V-4 actually
      // forbids a custom→terminal edge). No connection is emitted here, and
      // canonicalizeGraph strips such cosmetic edges from hand-built graphs.
    }
  }

  // 6. terminals (executionMethod defaults to the engine default when omitted)
  let stopBlockId: string | null = null;
  for (const t of ast.terminals) {
    if (t.kind === 'stop') {
      const b = mk('stop_loss', { name: 'stop', executionMethod: 'condition_based', ...structuredCloneLit(t.params) as Record<string, unknown> }, 0);
      stopBlockId = b.id;
    } else {
      mk('take_profit', { name: 'take', executionMethod: 'condition_based', ...structuredCloneLit(t.params) as Record<string, unknown> }, 0);
    }
  }
  // link TP→SL when exactly one of each (matches hand-built convention)
  const tps = blocks.filter(b => b.type === 'take_profit');
  if (stopBlockId && tps.length === 1 && tps[0].data.linkedStopLossBlockId === undefined) {
    tps[0].data.linkedStopLossBlockId = stopBlockId;
  }

  // 7. raw blocks (verbatim, ids preserved). References to script symbols
  //    are written as "@sym" by the printer — resolve them to compiled ids.
  const resolveRef = (v: unknown, line: number): unknown => {
    if (typeof v !== 'string' || !v.startsWith('@') || rawIds.has(v)) return v;
    const sym = v.slice(1);
    const id = symToId.get(sym);
    if (!id) {
      diagnostics.push({ line, col: 1, message: `raw block references unknown symbol '@${sym}'` });
      return v;
    }
    return id;
  };
  for (const r of ast.raws) {
    const data = JSON.parse(JSON.stringify(r.json.data ?? {})) as Record<string, unknown>;
    for (const f of REF_DATA_FIELDS) if (f in data) data[f] = resolveRef(data[f], r.line);
    blocks.push({
      ...JSON.parse(JSON.stringify(r.json)),
      id: r.json.id, type: r.json.type, data,
      position: r.json.position ?? pos(0),
      connections: {
        inputs: (r.json.connections?.inputs ?? []).map(x => String(resolveRef(x, r.line))),
        outputs: (r.json.connections?.outputs ?? []).map(x => String(resolveRef(x, r.line))),
      },
    });
  }

  if (diagnostics.length > 0) return { ok: false, blocks: null, diagnostics };
  return { ok: true, blocks, diagnostics };
}

function structuredCloneLit(v: Literal | Record<string, Literal>): unknown {
  return JSON.parse(JSON.stringify(v ?? {}));
}

// ── Printer: TSL blocks → script ───────────────────────────────────────────

const REF_DATA_FIELDS = [
  'leftInputBlockId', 'rightInputBlockId',
  'leftPriceDataBlockId', 'rightPriceDataBlockId', 'targetEntryBlockId',
  'qtyCalculatorBlockId', 'slCalculatorBlockId', 'tpCalculatorBlockId',
  'linkedStopLossBlockId',
];

function litToText(v: unknown, indent = ''): string {
  if (typeof v === 'string') {
    // bare identifier when safe (enum-ish values) — fewer tokens, same parse
    if (/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(v) && !['true', 'false', 'null', 'use', 'ts', 'and', 'or', 'not', 'when'].includes(v)) return v;
    return JSON.stringify(v);
  }
  if (typeof v === 'number' || typeof v === 'boolean' || v === null) return JSON.stringify(v);
  if (Array.isArray(v)) return '[' + v.map(x => litToText(x, indent)).join(', ') + ']';
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o);
  if (keys.length === 0) return '{}';
  const inner = keys.map(k => `${indent}  ${/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(k) ? k : JSON.stringify(k)}: ${litToText(o[k], indent + '  ')}`);
  return '{\n' + inner.join(',\n') + '\n' + indent + '}';
}

function paramsToText(data: Record<string, unknown>, skip: Set<string>): string {
  const parts: string[] = [];
  for (const k of Object.keys(data)) {
    if (skip.has(k) || COSMETIC_DATA_KEYS.has(k)) continue;
    const v = data[k];
    if (v === undefined) continue;
    parts.push(`${k}: ${litToText(v)}`);
  }
  return parts.join(', ');
}

/** Printer options. `fold: true` collapses canonical-registry custom blocks
 *  to one-line `use id(params)` references (the compact/token-minimal view).
 *  DEFAULT is fold:false — FULL code shown inline, including canonical
 *  bodies, so humans and the AI reason over the complete logic. Both forms
 *  compile to equivalent graphs (round-trip tested in both modes). */
/** P12.5 — strip comments from custom-block code, string/template/regex-
 *  literal aware. Used for AI prompts ONLY (the model needs logic, not the
 *  reviewer prose that documents parity history). NOT round-trip-preserving
 *  by design: stripped code is a different string, so the human-facing view
 *  and the round-trip laws always use the unstripped form. */
export function stripCodeComments(code: string): string {
  let out = '';
  let i = 0;
  const n = code.length;
  let prevSignificant = '';
  while (i < n) {
    const ch = code[i];
    if (ch === '"' || ch === "'" || ch === '`') {
      const q = ch;
      out += ch; i++;
      while (i < n) {
        out += code[i];
        if (code[i] === '\\') { i++; if (i < n) out += code[i]; i++; continue; }
        if (code[i] === q) { i++; break; }
        i++;
      }
      prevSignificant = q;
      continue;
    }
    if (ch === '/' && code[i + 1] === '/') {
      while (i < n && code[i] !== '\n') i++;
      continue;
    }
    if (ch === '/' && code[i + 1] === '*') {
      i += 2;
      while (i < n && !(code[i] === '*' && code[i + 1] === '/')) i++;
      i += 2;
      continue;
    }
    if (ch === '/' && (prevSignificant === '' || '=([{,;:!&|?+-*%~^<>'.includes(prevSignificant))) {
      // regex literal — copy verbatim to the closing unescaped '/'
      out += ch; i++;
      let inClass = false;
      while (i < n) {
        out += code[i];
        if (code[i] === '\\') { i++; if (i < n) out += code[i]; i++; continue; }
        if (code[i] === '[') inClass = true;
        else if (code[i] === ']') inClass = false;
        else if (code[i] === '/' && !inClass) { i++; break; }
        i++;
      }
      prevSignificant = '/';
      continue;
    }
    out += ch;
    if (!/\s/.test(ch)) prevSignificant = ch;
    i++;
  }
  // drop now-empty lines (comment-only lines) and collapse blank runs
  return out
    .split('\n')
    .map(l => (l.trim() === '' ? '' : l.replace(/\s+$/, '')))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+/, '')
    .replace(/\n+$/, '');
}

export interface PrintOptions { fold?: boolean; stripComments?: boolean }

// ── P12.6 — prompt representation routing (user-locked policy) ─────────────
//
// Do NOT use one TSS representation for every AI situation — choose by task.
// This function is the single authority; call sites must route through it
// so the policy stays enforced code, not tribal knowledge.
//
//   REFINE_FULL_STRATEGY                  full code, comments stripped
//     (refinement needs the executable logic, minus reviewer prose)
//   CUSTOM_BLOCK_EDIT_WITH_STRATEGY_DEPENDENCY  full code, stripped
//     (block depends on broader behavior -> same as refine)
//   GENERATE_NEW_STRATEGY / HIGH_LEVEL_MUTATION  compact use-refs
//     (architecture-level work doesn't need parity-validated bodies;
//      note: fresh generation carries NO parent graph at all — compact
//      applies wherever a graph IS embedded, e.g. mutation seeds)
//   CUSTOM_BLOCK_EDIT / DEBUG_RUNTIME_ERROR      returns null —
//     block-only context REQUIRES the block-patch protocol (model returns
//     one block, server splices into the parent, recompiles, validates).
//     NOT YET IMPLEMENTED: until then, callers must fall back to
//     CUSTOM_BLOCK_EDIT_WITH_STRATEGY_DEPENDENCY (full stripped). Faking
//     block-only routing with a whole-strategy output contract would make
//     the model hallucinate the rest of the graph.
//   DEBUG_COMPILE_ERROR                          returns null —
//     no graph payload; the retry loop sends the verbatim failing script
//     + line:col diagnostics (already implemented in generate-strategy).
export type PromptTask =
  | 'REFINE_FULL_STRATEGY'
  | 'GENERATE_NEW_STRATEGY'
  | 'HIGH_LEVEL_MUTATION'
  | 'CUSTOM_BLOCK_EDIT'
  | 'CUSTOM_BLOCK_EDIT_WITH_STRATEGY_DEPENDENCY'
  | 'DEBUG_COMPILE_ERROR'
  | 'DEBUG_RUNTIME_ERROR';

// ── P14 — conservative auto-route: large parent graphs use TSS ────────────
//
// JSON refinement of a large strategy emits ~10k output tokens and blows
// the gateway wall (the 504 class). TSS output is 5-10x smaller. This
// resolver decides — deterministically and with a visible SOURCE label —
// whether a given REFINE / parent-based generation should use TSS.
//
// Conservative by design (user-locked):
//   • manual toggle ALWAYS wins ('manual').
//   • otherwise auto-route ONLY when the parent graph is large
//     (> TSS_AUTO_BLOCK_THRESHOLD blocks) AND a parent exists (REFINE /
//     parent-based generation) — 'auto_large_graph'.
//   • otherwise JSON ('off'). Small/simple strategies are never forced.
// TSS stays beta-gated; this is NOT a global default flip. JSON remains
// the fallback when a TSS compile fails (handled in generate-strategy).
//
// Default 20 (user-locked 2026-06-11): narrow/conservative — only
// genuinely large strategies (the JSON-output-504 cases like the 34-block
// FTMO Sweep) auto-route; medium/simple graphs stay JSON until we have
// more evidence that TSS helps them too. Overridable per run via
// OrchestratorRequest.scriptAutoRouteThreshold.
export const TSS_AUTO_BLOCK_THRESHOLD = 20;

export type ScriptModeSource = 'manual' | 'auto_large_graph' | 'off';

export function resolveScriptMode(args: {
  manualScriptMode: boolean;
  parentBlockCount: number | null; // null when no parent (fresh generation)
  /** Auto-route block threshold (strict >). Defaults to the constant. */
  threshold?: number;
}): { scriptMode: boolean; source: ScriptModeSource } {
  if (args.manualScriptMode) return { scriptMode: true, source: 'manual' };
  const threshold = args.threshold ?? TSS_AUTO_BLOCK_THRESHOLD;
  if (args.parentBlockCount !== null && args.parentBlockCount > threshold) {
    return { scriptMode: true, source: 'auto_large_graph' };
  }
  return { scriptMode: false, source: 'off' };
}

export function printOptionsForTask(task: PromptTask): PrintOptions | null {
  switch (task) {
    case 'REFINE_FULL_STRATEGY':
    case 'CUSTOM_BLOCK_EDIT_WITH_STRATEGY_DEPENDENCY':
      return { stripComments: true };
    case 'GENERATE_NEW_STRATEGY':
    case 'HIGH_LEVEL_MUTATION':
      return { fold: true, stripComments: true };
    case 'CUSTOM_BLOCK_EDIT':
    case 'DEBUG_RUNTIME_ERROR':
    case 'DEBUG_COMPILE_ERROR':
      return null; // snippet-only / patch-protocol routes — no whole-graph print
  }
}

export function printGraph(blocksIn: Array<Record<string, unknown>>, opts?: PrintOptions): PrintResult {
  // Sugar is an optimization, never permission to change execution. Check the
  // actual compiled projection, including ordered inputs, native dispatch,
  // disabled flags and provenance. Unknown constructs retain their full graph.
  try {
    const printed = printGraphSugar(blocksIn, opts);
    const compiled = compileScript(printed.text);
    if (compiled.ok && compiled.blocks && stableStringify(canonicalizeGraph(blocksIn, true)) ===
        stableStringify(canonicalizeGraph(compiled.blocks as unknown as Array<Record<string, unknown>>, true))) return printed;
  } catch { /* Unsupported sugar falls back to explicit graph records. */ }
  const blocks = blocksIn.map(b => {
    const copy = JSON.parse(JSON.stringify(b));
    // Editor caches are not provenance and would recursively embed old scripts.
    if (copy.metadata) {
      delete copy.metadata.sourceText; delete copy.metadata.sourceHash;
      if (!Object.keys(copy.metadata).length) delete copy.metadata;
    }
    return copy;
  });
  return { text: '// Lossless graph form: raw records preserve execution order, wiring and provenance.\n' +
    blocks.map(b => `raw ${JSON.stringify(b)}`).join('\n') + '\n',
    stats: { sugared: 0, generic: blocks.length, total: blocks.length } };
}

function printGraphSugar(blocksIn: Array<Record<string, unknown>>, opts?: PrintOptions): PrintResult {
  const fold = opts?.fold === true;
  const blocks = blocksIn as unknown as TslBlock[];
  const byId = new Map(blocks.map(b => [b.id, b]));
  const lines: string[] = [];
  let sugared = 0, generic = 0;

  // symbol assignment: slug of data.name, deduped; fallback b<idx>
  const symOf = new Map<string, string>();
  const used = new Set<string>(['close', 'open', 'high', 'low', 'and', 'or', 'not', 'when', 'use', 'ts', 'true', 'false', 'null', 'long', 'short', 'qty', 'sl', 'tp']);
  blocks.forEach((b, i) => {
    if (b.type === 'strategy_config') return;
    let s = slugify(String(b.data?.name ?? '')) || `b${i + 1}`;
    if (used.has(s)) {
      let k = 2;
      while (used.has(`${s}${k}`)) k++;
      s = `${s}${k}`;
    }
    used.add(s);
    symOf.set(b.id, s);
  });

  // 1. strategy_config
  const cfg = blocks.find(b => b.type === 'strategy_config');
  if (cfg) {
    const name = String(cfg.data.name ?? cfg.data.title ?? 'Strategy');
    lines.push(`strategy ${JSON.stringify(name)} {`);
    for (const k of Object.keys(cfg.data)) {
      if (k === 'name') continue;
      lines.push(`  ${k}: ${litToText(cfg.data[k], '  ')}`);
    }
    lines.push('}', '');
    sugared++;
  }

  const printed = new Set<string>(cfg ? [cfg.id] : []);
  const isCustom = (b: TslBlock) => b.type === 'custom';
  const customRoleKeyword = (bt: string): string | null => {
    switch (bt) {
      case 'pattern': return 'pattern';
      case 'filter': return 'filter';
      case 'indicator': return 'ind';
      case 'qty_calculator': return 'calc';
      case 'sl_calculator': return 'calc';
      case 'tp_calculator': return 'calc';
      case 'risk_rule': return 'risk';
      default: return null;
    }
  };

  // 2. indicator-ish blocks (generic param form — total for flat data)
  for (const b of blocks) {
    if (printed.has(b.id)) continue;
    if (!INDICATOR_BLOCK_TYPES.has(b.type)) continue;
    lines.push(`ind ${symOf.get(b.id)} = ${b.type}(${paramsToText(b.data, new Set())})`);
    printed.add(b.id);
    sugared++;
  }
  if (blocks.some(b => INDICATOR_BLOCK_TYPES.has(b.type))) lines.push('');

  // 3. customs
  for (const b of blocks) {
    if (printed.has(b.id) || !isCustom(b)) continue;
    const bt = String(b.data.blockType ?? '');
    const kw = customRoleKeyword(bt);
    if (!kw) continue; // unknown custom subtype → generic fallback below
    const params = (b.data.parameters && typeof b.data.parameters === 'object') ? { ...(b.data.parameters as Record<string, unknown>) } : {};
    const canonicalId = typeof params.__canonical === 'string' ? params.__canonical : undefined;
    // TSL migration — a custom block prints from its canonical TSL text
    // (data.tslAst re-printed, so the projection law holds even if stored
    // text drifted); per-trade marker blocks always print as `use`.
    const ast = b.data.tslAst as SafeTslBlock | undefined;
    const canonText = ast ? printTslBlock(ast) : undefined;
    const reg = (canonicalId && CANONICAL_BY_ID.get(canonicalId)) || (canonText ? CANONICAL_BY_TSL.get(canonText) : undefined);
    delete params.__canonical;
    const sym = symOf.get(b.id);
    const head =
      kw === 'risk' ? 'risk' :
      kw === 'calc' ? `calc ${sym} =${bt === 'sl_calculator' ? ' sl' : bt === 'tp_calculator' ? ' tp' : ' qty'}` :
      kw === 'ind' ? `ind ${sym} =` : `${kw} ${sym} =`;
    const regTsl = reg ? CANONICAL_TSL.get(reg.id) : undefined;
    const foldable = !!reg && reg.blockType === bt
      && (regTsl ? canonText === regTsl.canonicalText : !ast); // per-trade markers have no AST to compare
    if ((fold || !ast) && foldable) {
      // per-trade marker blocks fold in BOTH modes (there is no meaningful
      // inline body); per-bar canonical blocks fold only in folded mode.
      const shownParams = { ...params };
      // drop params equal to registry defaults (token savings, same compile)
      for (const dk of Object.keys(reg!.defaults ?? {})) {
        if (stableStringify(shownParams[dk]) === stableStringify((reg!.defaults as Record<string, unknown>)[dk])) delete shownParams[dk];
      }
      lines.push(`${head} use ${reg!.id}(${paramsToText(shownParams, new Set())})`);
      sugared++;
    } else if (ast && canonText) {
      // inline TSL: print the INNER statements (the header/braces come from
      // the statement's role keyword) with call-site params up front.
      const inner = canonText.split('\n').slice(1, -1).map((l) => l.replace(/^  /, ''));
      // shown params: only overrides vs the declared defaults inside the text.
      const declared: Record<string, unknown> = {};
      for (const [k, spec] of Object.entries(ast.params ?? {})) declared[k] = (spec as { default?: unknown }).default;
      const shownParams: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(params)) {
        if (stableStringify(v) !== stableStringify(declared[k])) shownParams[k] = v;
      }
      lines.push(`${head} tsl (${paramsToText(shownParams, new Set())}) {`);
      for (const l of inner) lines.push(`  ${l}`);
      lines.push('}');
      generic++;
    } else {
      // Legacy JS block (quarantined at runtime): print as `use`-less generic —
      // emit the marker-less params so the graph stays representable; the
      // compile side will reject it with the migration diagnostic, which is
      // the honest round-trip for dead blocks.
      lines.push(`${head} ts (${paramsToText(params, new Set())}) {`);
      lines.push(opts?.stripComments ? stripCodeComments(String(b.data.code ?? '')) : String(b.data.code ?? ''));
      lines.push('}');
      generic++;
    }
    printed.add(b.id);
  }
  if (blocks.some(isCustom)) lines.push('');

  // 4. expressions → cond
  const operandText = (b: TslBlock, side: 'left' | 'right'): string | null => {
    const src = b.data[`${side}Source`];
    if (src === 'price') return String(b.data[`${side}PriceSource`] ?? 'close');
    if (src === 'static') return litToText(b.data[`${side}Value`]);
    if (src === 'input') {
      const refId = String(b.data[`${side}InputBlockId`] ?? '');
      const sym = symOf.get(refId);
      if (!sym) return null;
      const port = b.data[`${side}OutputPort`];
      return port && port !== 'value' ? `${sym}.${port}` : sym;
    }
    return null;
  };
  for (const b of blocks) {
    if (printed.has(b.id) || b.type !== 'expression') continue;
    const lt = operandText(b, 'left');
    const rt = operandText(b, 'right');
    const op = String(b.data.operator ?? '');
    const extraKeys = Object.keys(b.data).filter(k =>
      !COSMETIC_DATA_KEYS.has(k) &&
      !['operator', 'leftSource', 'leftPriceSource', 'leftValue', 'leftInputBlockId', 'leftOutputPort',
        'rightSource', 'rightPriceSource', 'rightValue', 'rightInputBlockId', 'rightOutputPort'].includes(k));
    if (lt && rt && COMPARATORS.has(op) && extraKeys.length === 0) {
      lines.push(`cond ${symOf.get(b.id)} = ${lt} ${op} ${rt}`);
      printed.add(b.id);
      sugared++;
    }
    // else: falls to generic raw below
  }
  if (blocks.some(b => b.type === 'expression')) lines.push('');

  // 5. gates + entries → entry … when …
  const gateExprText = (g: TslBlock, depth: number): string | null => {
    if (depth > 6) return null;
    const inputs = g.connections.inputs.map(id => byId.get(id)).filter(Boolean) as TslBlock[];
    if (inputs.length === 0) return null;
    const parts: string[] = [];
    for (const inp of inputs) {
      if (inp.type === 'logic_gate') {
        const sub = gateExprText(inp, depth + 1);
        if (sub === null) return null;
        parts.push(`(${sub})`);
        printed.add(inp.id);
      } else {
        const sym = symOf.get(inp.id);
        if (!sym || !printed.has(inp.id)) return null; // input not printable as a symbol
        parts.push(sym);
      }
    }
    const joiner = String(g.data.type ?? 'AND') === 'OR' ? ' or ' : ' and ';
    const body = parts.length === 1 ? parts[0] : parts.join(joiner);
    return g.data.invert === true ? `not (${body})` : body;
  };
  const ENTRY_STD_KEYS = new Set([
    'direction', 'entryType', 'useStrategyDefaults',
    'qtyCalculatorBlockId', 'slCalculatorBlockId', 'tpCalculatorBlockId',
  ]);
  for (const b of blocks) {
    if (printed.has(b.id) || b.type !== 'entry') continue;
    const inputs = b.connections.inputs
      .map(id => byId.get(id))
      .filter((x): x is TslBlock => !!x && !(x.type === 'custom' && /calculator$/.test(String(x.data.blockType ?? ''))));
    let whenText: string | null = null;
    if (inputs.length === 1) {
      const src = inputs[0];
      if (src.type === 'logic_gate') {
        whenText = gateExprText(src, 0);
        if (whenText !== null) printed.add(src.id);
      } else if (printed.has(src.id)) {
        whenText = symOf.get(src.id) ?? null;
      }
    }
    if (whenText === null) continue; // generic fallback
    let lineTxt = `entry ${String(b.data.direction ?? 'long')} when ${whenText}`;
    const hasCalc = ['qtyCalculatorBlockId', 'slCalculatorBlockId', 'tpCalculatorBlockId'].some(f => typeof b.data[f] === 'string');
    for (const [field, clause] of [['qtyCalculatorBlockId', 'qty'], ['slCalculatorBlockId', 'sl'], ['tpCalculatorBlockId', 'tp']] as const) {
      const ref = b.data[field];
      if (typeof ref === 'string' && symOf.get(ref)) lineTxt += ` ${clause} ${symOf.get(ref)}`;
    }
    // Extra behavioral fields (sizing overrides etc.) ride an explicit
    // param group so the round-trip is lossless. useStrategyDefaults is
    // derived (!calc-clauses) — included only when it deviates.
    const extras: Record<string, unknown> = {};
    for (const k of Object.keys(b.data)) {
      if (ENTRY_STD_KEYS.has(k) || COSMETIC_DATA_KEYS.has(k)) continue;
      extras[k] = b.data[k];
    }
    if (b.data.entryType !== undefined && b.data.entryType !== 'market') extras.entryType = b.data.entryType;
    if (typeof b.data.useStrategyDefaults === 'boolean' && b.data.useStrategyDefaults !== !hasCalc) {
      extras.useStrategyDefaults = b.data.useStrategyDefaults;
    }
    if (Object.keys(extras).length > 0) lineTxt += ` (${paramsToText(extras, new Set())})`;
    lines.push(lineTxt);
    printed.add(b.id);
    sugared++;
  }
  if (blocks.some(b => b.type === 'entry')) lines.push('');

  // 6. terminals
  for (const b of blocks) {
    if (printed.has(b.id)) continue;
    if (b.type === 'stop_loss') {
      lines.push(`stop (${paramsToText(b.data, new Set())})`);
      printed.add(b.id); sugared++;
    } else if (b.type === 'take_profit') {
      // linkedStopLossBlockId is derivable when exactly one stop exists
      const stops = blocks.filter(x => x.type === 'stop_loss');
      const skip = new Set<string>();
      if (stops.length === 1 && b.data.linkedStopLossBlockId === stops[0].id) skip.add('linkedStopLossBlockId');
      lines.push(`take (${paramsToText(b.data, skip)})`);
      printed.add(b.id); sugared++;
    }
  }

  // 7. anything unprinted → raw escape hatch (total printing, never lossy).
  //    References to symbol-bearing blocks become "@sym" so the compiler can
  //    re-bind them to the freshly assigned ids of the sugared blocks.
  for (const b of blocks) {
    if (printed.has(b.id)) continue;
    const toRef = (id: string) => symOf.has(id) ? '@' + symOf.get(id) : id;
    const data = JSON.parse(JSON.stringify(b.data)) as Record<string, unknown>;
    for (const f of REF_DATA_FIELDS) {
      if (typeof data[f] === 'string' && symOf.has(String(data[f]))) data[f] = '@' + symOf.get(String(data[f]));
    }
    const obj = {
      id: b.id, type: b.type, data, position: b.position,
      connections: {
        inputs: b.connections.inputs.map(toRef),
        outputs: b.connections.outputs.map(toRef),
      },
    };
    lines.push(`raw ${JSON.stringify(obj)}`);
    printed.add(b.id);
    generic++;
  }

  return {
    text: lines.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd() + '\n',
    stats: { sugared, generic, total: blocks.length },
  };
}

// ── Canonical graph equivalence (the round-trip law's equality) ────────────
//
// Semantic equality: same multiset of blocks (type + behavioral data) with
// identical reference topology. Ids are relabeled canonically (they are
// arbitrary handles), positions/metadata/cosmetic fields stripped (layout
// + display chrome; enrichBlocks backfills names). The engine consumes
// references, which relabeling preserves exactly.

export function canonicalizeGraph(blocksIn: Array<Record<string, unknown>>, preserveOrder = true): TslBlock[] {
  const blocks = (blocksIn as unknown as TslBlock[]).map(b => ({
    id: b.id,
    type: b.type,
    data: JSON.parse(JSON.stringify(b.data ?? {})) as Record<string, unknown>,
    position: { x: 0, y: 0 },
    ...(b.metadata ? { metadata: Object.fromEntries(Object.entries(b.metadata).filter(([k]) => k !== 'sourceText' && k !== 'sourceHash')) } : {}),
    connections: {
      inputs: [...(b.connections?.inputs ?? [])],
      outputs: [...(b.connections?.outputs ?? [])],
    },
  }));
  // strip cosmetic data keys (strategy_config keeps everything — its fields
  // are behavioral config, and 'name' there is the strategy identity)
  for (const b of blocks) {
    if (b.type === 'strategy_config') continue;
    for (const k of COSMETIC_DATA_KEYS) delete b.data[k];
    // __canonical is registry bookkeeping, not behavior — code equality is
    // the behavioral fact, and that's compared verbatim.
    const params = b.data.parameters;
    if (params && typeof params === 'object' && !Array.isArray(params)) {
      const p = { ...(params as Record<string, unknown>) };
      // Native per-trade blocks execute by this marker. Per-bar AST-backed
      // blocks execute their AST, so their registry marker is only bookkeeping.
      if (b.data.tslAst && ['indicator', 'pattern', 'analysis', 'filter', 'signal_generator'].includes(String(b.data.blockType))) delete p.__canonical;
      b.data.parameters = p;
    }
    // engine-default normalization: absent field ≡ engine default
    if ((b.type === 'stop_loss' || b.type === 'take_profit') && b.data.executionMethod === undefined) {
      b.data.executionMethod = 'condition_based';
    }
    if (b.type === 'entry' && b.data.entryType === undefined) b.data.entryType = 'market';
    if (b.type === 'expression') {
      if (b.data.leftSource === 'input' && b.data.leftOutputPort === undefined) b.data.leftOutputPort = 'value';
      if (b.data.rightSource === 'input' && b.data.rightOutputPort === undefined) b.data.rightOutputPort = 'value';
    }
    if (b.type === 'logic_gate' && b.data.invert === undefined) b.data.invert = false;
    if (b.type === 'custom' && b.data.outputTypes === undefined) {
      const bt = String(b.data.blockType ?? '');
      b.data.outputTypes =
        bt === 'pattern' ? ['patterns'] :
        bt === 'filter' ? ['signals'] :
        bt === 'qty_calculator' ? ['qty'] :
        bt === 'risk_rule' ? ['risk_action'] : ['value'];
    }
  }
  // With exactly ONE stop_loss, a TP's linkedStopLossBlockId pointing at it
  // is the engine's unambiguous default — semantically identical to absent.
  const stops = blocks.filter(b => b.type === 'stop_loss');
  if (stops.length === 1) {
    for (const b of blocks) {
      if (b.type === 'take_profit' && b.data.linkedStopLossBlockId === stops[0].id) {
        delete b.data.linkedStopLossBlockId;
      }
    }
  }
  // Per-trade calculators dispatch via id POINTERS (entry.data.*CalculatorBlockId),
  // not canvas edges — hand-built graphs often carry a cosmetic calc→entry
  // edge that the server validator forbids. Strip it from the canonical form.
  const entryIds = new Set(blocks.filter(b => b.type === 'entry').map(b => b.id));
  const calcIds = new Set(blocks.filter(b => b.type === 'custom' && /calculator$/.test(String(b.data.blockType ?? ''))).map(b => b.id));
  for (const b of preserveOrder ? [] : blocks) {
    if (calcIds.has(b.id)) b.connections.outputs = b.connections.outputs.filter(o => !entryIds.has(o));
    if (entryIds.has(b.id)) b.connections.inputs = b.connections.inputs.filter(i => !calcIds.has(i));
  }
  // initial hash: type + data with refs masked
  const refMask = (b: TslBlock): string => {
    const d = { ...b.data };
    for (const f of REF_DATA_FIELDS) if (f in d) d[f] = '#ref';
    return b.type + '|' + stableStringify(d);
  };
  let hashes = new Map<string, string>(blocks.map(b => [b.id, fnv1a(refMask(b))]));
  // Weisfeiler-Lehman refinement over the connection topology + data refs
  for (let round = 0; round < 3; round++) {
    const next = new Map<string, string>();
    for (const b of blocks) {
      const nbr = [
        ...b.connections.inputs.map(i => 'i:' + (hashes.get(i) ?? '?')),
        ...b.connections.outputs.map(o => 'o:' + (hashes.get(o) ?? '?')),
        ...REF_DATA_FIELDS.filter(f => typeof b.data[f] === 'string').map(f => f + ':' + (hashes.get(String(b.data[f])) ?? '?')),
      ].sort();
      next.set(b.id, fnv1a(hashes.get(b.id)! + '|' + nbr.join(',')));
    }
    hashes = next;
  }
  // canonical order: by (type, refMask, wlHash, original index) — stable
  const order = blocks
    .map((b, i) => ({ b, i, key: b.type + '|' + refMask(b) + '|' + hashes.get(b.id) }))
    .sort((a, z) => preserveOrder ? a.i - z.i : a.key < z.key ? -1 : a.key > z.key ? 1 : a.i - z.i);
  const relabel = new Map<string, string>();
  order.forEach((o, idx) => relabel.set(o.b.id, `n${idx + 1}`));
  const out: TslBlock[] = order.map(o => {
    const b = o.b;
    const data = { ...b.data };
    for (const f of REF_DATA_FIELDS) {
      if (typeof data[f] === 'string' && relabel.has(String(data[f]))) data[f] = relabel.get(String(data[f]));
    }
    return {
      id: relabel.get(b.id)!,
      type: b.type,
      data,
      position: { x: 0, y: 0 },
      ...(b.metadata && Object.keys(b.metadata).length ? { metadata: b.metadata } : {}),
      connections: {
        inputs: b.connections.inputs.map(i => relabel.get(i) ?? i),
        outputs: b.connections.outputs.map(i => relabel.get(i) ?? i),
      },
    };
  });
  return out;
}

export function graphsEquivalent(a: Array<Record<string, unknown>>, b: Array<Record<string, unknown>>): { equal: boolean; detail?: string } {
  const ca = canonicalizeGraph(a, true);
  const cb = canonicalizeGraph(b, true);
  const sa = stableStringify(ca);
  const sb = stableStringify(cb);
  if (sa === sb) return { equal: true };
  // produce a small, useful diff for the test harness
  if (ca.length !== cb.length) return { equal: false, detail: `block count ${ca.length} vs ${cb.length}` };
  for (let i = 0; i < ca.length; i++) {
    const xa = stableStringify(ca[i]);
    const xb = stableStringify(cb[i]);
    if (xa !== xb) {
      let d = 0;
      while (d < xa.length && d < xb.length && xa[d] === xb[d]) d++;
      const ctx = (s: string) => s.slice(Math.max(0, d - 80), d + 120);
      return {
        equal: false,
        detail: `first differing block #${i} (${ca[i].type} vs ${cb[i].type}) at char ${d}:\n  A: …${ctx(xa)}…\n  B: …${ctx(xb)}…`,
      };
    }
  }
  return { equal: false, detail: 'differs in unknown way' };
}

/** Stable hash of a graph's canonical form — used for sourceText staleness. */
export function graphSemanticHash(blocks: Array<Record<string, unknown>>): string {
  return fnv1a(stableStringify(canonicalizeGraph(blocks, true)));
}

// ── AI prompt assets: grammar spec + exemplars ─────────────────────────────
//
// Kept SMALL and STABLE on purpose (prompt-cache friendly). The model learns
// the language from this spec + the exemplars + compiler diagnostics on
// retry. The compiler is the authority — the model is never trusted to
// "understand" the language; the compiled graph proves correctness through
// the validator, the no-op-repair gate, and the backtest.

export const SCRIPT_GRAMMAR_PROMPT = `TSS (TRADEATORY STRATEGY SCRIPT) — compact language that compiles to the TSL block graph (the engine executes TSL only). One statement per line. // comments.

strategy "Name" { key: value … }     — strategy_config. Common keys: initial_capital, currency, pyramiding, maxPositions, commission_type ("percentage"|"per_contract"), commission_value, slippage, intradayOnly, session, cooldownBarsAfterExit, maxHoldBars, default_qty_type, default_qty_value. Nested objects allowed: propFirm: { … }.
ind name = <block_type>(key: value, …)  — indicator block. block_type ∈ moving_average | momentum_oscillator | trend | volatility | volume | time_filter | chart_patterns | candlestick_patterns. Params are the block's data fields, e.g. ind ema200 = moving_average(indicator: ema, period: 200, source: close) · ind rsi14 = momentum_oscillator(indicator: rsi, period: 14, overbought: 70, oversold: 30) · ind adx14 = trend(indicator: adx, period: 14, threshold: 20) · ind sess = time_filter(filterType: session, sessions: {american: true}, timezone: "UTC").
cond name = <left> <op> <right>      — expression block. left: open|high|low|close or a symbol (optionally sym.port). right: same, or a NUMBER (always numeric — never true/false). op: > >= < <= == != crosses_above crosses_below. e.g. cond bull = close > ema200 · cond inSess = sess > 0 · cond osold = rsi14 < 30.
pattern name = tsl (param: v, …) { …TSL statements… }   — custom per-bar pattern in SAFE TSL (never JavaScript). Body: one statement per line — param <name>: int|float <min>..<max> = <default> · state <name> = <init> -> <updateExpr> · value: <numericExpr> · signal: <booleanExpr>. Vocabulary: open high low close volume hl2 hlc3 ohlc4 · sma/ema/rma/wma/rsi/stdev/highest/lowest/change/roc(src, n) · atr(n) tr() htfema1(htfMinutes, period) · prev(expr, k) · state.<name> (previous bar's committed value) · + - * / min max abs sign neg nz · > < >= <= == != and or not · cond ? a : b · crossover/crossunder(a, b). Anything else is a parse error.
filter name = tsl (…) { … }          — custom per-bar filter, same TSL body form.
ind name = tsl (…) { … }             — custom TSL indicator, same body form (value: required).
calc name = qty use <canonical_id>(…) — qty calculator: use-reference ONLY (native engine implementation; cannot be written inline).
risk use <canonical_id>(…)            — risk_rule: use-reference ONLY (native engine implementation; cannot be written inline).
… = use <canonical_id>(param: v, …)  — reference a parity-validated registry block by id. Tune its PARAMS; to change per-bar internals, write your own tsl block instead. Registry:
${CANONICAL_BLOCKS.map(c => `  ${c.id} (${c.blockType}) — ${c.description}`).join('\n')}
entry long|short when condA and condB and (x or y) [qty calcName]   — builds the logic gate + entry. Conditions must be cond/pattern/filter names. Mixing and/or needs parentheses.
stop (stopType: atr_based, atrPeriod: 14, atrMultiplier: 1.5)        — stop_loss block data.
take (tpType: risk_reward, riskRewardRatio: 2)                       — take_profit block data.
raw {"id":"...","type":"...","data":{...},"connections":{"inputs":[],"outputs":[]}} — exact graph record. A raw-only script includes one strategy_config instead of a strategy header; preserve IDs, ordering, wiring, and provenance when editing it.

Custom-block rules: SAFE TSL ONLY — host JavaScript ('ts { … }') is REJECTED at compile; the engine never executes host code. TSL is deterministic by construction (no loops, no functions, no I/O). Example inline block:
  pattern sweepL = tsl () {
    param lookback: int 2..200 = 30
    value: ((low < prev(lowest(low, lookback), 1)) and (close > prev(lowest(low, lookback), 1))) ? 1 : 0
    signal: (low < prev(lowest(low, lookback), 1)) and (close > prev(lowest(low, lookback), 1))
  }
When writing a NEW strategy, PREFER \`use <canonical_id>(params)\` references for logic the registry already provides; write inline tsl blocks only for genuinely novel per-bar logic.
Output ONLY the TSS script (no prose, no JSON), inside a \`\`\`tss fenced block.`;

export const SCRIPT_EXEMPLAR = `\`\`\`tss
strategy "Intraday RSI Fade" {
  initial_capital: 10000, currency: USD, pyramiding: 1, maxPositions: 1,
  intradayOnly: true, session: european, noNewEntriesAfterMinutesBeforeClose: 30,
  maxHoldBars: 24, cooldownBarsAfterExit: 4,
  commission_type: percentage, commission_value: 0.06, slippage: 1,
  default_qty_type: percent_equity, default_qty_value: 10
}

ind rsi14 = momentum_oscillator(indicator: rsi, period: 14, overbought: 70, oversold: 30)
ind adx14 = trend(indicator: adx, period: 14, threshold: 20)
ind sess  = time_filter(filterType: session, sessions: {european: true}, timezone: "UTC")

cond oversold = rsi14 < 30
cond ranging  = adx14 < 20
cond inSess   = sess > 0

entry long when oversold and ranging and inSess

stop (stopType: atr_based, atrPeriod: 14, atrMultiplier: 2)
take (tpType: risk_reward, riskRewardRatio: 1.5)
\`\`\``;

/** Extract the fenced TSS script from a model response (```tss … ```;
 *  legacy ```tsl and bare fences tolerated). */
export function extractScript(text: string): string | null {
  const fence = text.match(/```(?:tss|tsl)?\s*\n([\s\S]*?)```/);
  if (fence) return fence[1];
  // tolerate fence-less responses that look like script
  if (/^\s*strategy\s+"/m.test(text)) return text;
  return null;
}

/** Render diagnostics for an AI repair retry (or a human). */
export function formatDiagnostics(diags: ScriptDiagnostic[]): string {
  return diags.slice(0, 12).map(d =>
    `line ${d.line}:${d.col} — ${d.message}${d.expected ? ` (expected ${d.expected})` : ''}${d.hint ? `. Hint: ${d.hint}` : ''}`
  ).join('\n');
}
